import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import companyLogo from '../../Assets/BooksCompl.png';

const BRAND = [79, 70, 229];
const BRAND_DARK = [0, 51, 102];
const MARGIN = 15;
const PAGE_BOTTOM = 275;

const formatValue = (value) => {
  if (value == null) return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  if (typeof value === 'number') return String(value);
  return String(value);
};

const formatMoney = (value, { negative = false } = {}) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return formatValue(value);
  const formatted = `$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return negative || num < 0 ? `-${formatted}` : formatted;
};

const isAssetField = (key) =>
  [
    'cash',
    'tradeNotes',
    'subtractAllowance',
    'inventories',
    'usGovObligations',
    'taxExemptSecurities',
    'otherCurrentAssets',
    'loansToStockholders',
    'mortgageRealEstateLoans',
    'otherInvestments',
    'depreciableAssets',
    'depletableAssets',
    'land',
    'intangibleAssets',
    'subtractAmortization',
    'otherAssets',
    'totalAssetValue',
    'filingFee',
  ].includes(key);

const drawHeader = (doc, { title, subtitle, preparedFor }) => {
  const pageWidth = doc.internal.pageSize.getWidth();

  try {
    doc.addImage(companyLogo, 'PNG', MARGIN, 12, 40, 15);
  } catch {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...BRAND_DARK);
    doc.text('BooksComply', MARGIN, 22);
  }

  const rightX = pageWidth - MARGIN;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...BRAND_DARK);
  doc.text('ANNUAL REPORT CHECKLIST', rightX, 18, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(title || 'Checklist', rightX, 25, { align: 'right' });

  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(subtitle, rightX, 31, { align: 'right' });
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Prepared for: ${preparedFor || 'N/A'}`, rightX, 37, { align: 'right' });
  doc.text(
    `Generated: ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })}`,
    rightX,
    42,
    { align: 'right' }
  );

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, 48, pageWidth - MARGIN, 48);

  return 56;
};

const addFooter = (doc, pageNumber, totalPages) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, pageHeight - 16, pageWidth - MARGIN, pageHeight - 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text('BooksComply — Annual Report Services', MARGIN, pageHeight - 10);
  doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - MARGIN, pageHeight - 10, {
    align: 'right',
  });
};

const ensureSpace = (doc, y, needed = 40) => {
  if (y + needed > PAGE_BOTTOM) {
    doc.addPage();
    return 20;
  }
  return y;
};

const drawSectionTitle = (doc, y, title) => {
  y = ensureSpace(doc, y, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_DARK);
  doc.text(String(title || 'Section').toUpperCase(), MARGIN, y);

  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y + 2, MARGIN + 28, y + 2);

  return y + 8;
};

const tableOptions = (startY) => ({
  startY,
  theme: 'striped',
  margin: { left: MARGIN, right: MARGIN },
  styles: {
    fontSize: 9,
    cellPadding: 3,
    textColor: [40, 40, 40],
    lineColor: [230, 230, 230],
    lineWidth: 0.2,
    valign: 'middle',
  },
  headStyles: {
    fillColor: BRAND,
    textColor: [255, 255, 255],
    fontStyle: 'bold',
    fontSize: 9,
  },
  alternateRowStyles: {
    fillColor: [245, 247, 255],
  },
  columnStyles: {
    0: { cellWidth: 65, fontStyle: 'bold', textColor: [60, 60, 80] },
    1: { cellWidth: 'auto' },
  },
});

const drawFieldTable = (doc, y, rows) => {
  if (!rows.length) return y;
  y = ensureSpace(doc, y, 30);
  doc.autoTable({
    ...tableOptions(y),
    head: [['Field', 'Value']],
    body: rows,
  });
  return doc.lastAutoTable.finalY + 10;
};

const drawDirectorTable = (doc, y, section, list) => {
  const fields = (section.fields || []).filter((f) => f.isActive !== false);
  if (!fields.length) return y;

  const head = fields.map((f) => f.label);
  const body = (list || []).map((item, index) =>
    fields.map((f) => {
      if (fields.length === 1 && f.key === 'name') {
        return formatValue(item?.[f.key]);
      }
      return formatValue(item?.[f.key]);
    })
  );

  // Prefixed row labels when useful
  const useIndex = body.length > 1;
  const finalHead = useIndex ? ['#', ...head] : head;
  const finalBody = useIndex
    ? body.map((row, i) => [String(i + 1), ...row])
    : body;

  y = ensureSpace(doc, y, 35);
  doc.autoTable({
    ...tableOptions(y),
    head: [finalHead],
    body: finalBody.length ? finalBody : [finalHead.map(() => '—')],
    columnStyles: useIndex
      ? { 0: { cellWidth: 12, halign: 'center', fontStyle: 'bold' } }
      : {},
  });
  return doc.lastAutoTable.finalY + 10;
};

const buildFieldsRows = (section, formData) => {
  const fields = (section.fields || []).filter((f) => f.isActive !== false);
  return fields.map((f) => {
    const raw = formData?.[f.key];
    const subtractKeys = ['subtractAllowance', 'subtractAmortization'];
    const value = isAssetField(f.key)
      ? formatMoney(raw, { negative: subtractKeys.includes(f.key) })
      : formatValue(raw);
    return [f.label, value];
  });
};

const drawSections = (doc, startY, checklist, formData) => {
  let y = startY;
  const sections = (checklist?.sections || [])
    .filter((s) => s.isActive !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  sections.forEach((section) => {
    y = drawSectionTitle(doc, y, section.title);

    if (section.type === 'fields') {
      y = drawFieldTable(doc, y, buildFieldsRows(section, formData));
      return;
    }

    if (section.type === 'select') {
      y = drawFieldTable(doc, y, [
        [section.label || 'Selection', formatValue(formData?.[section.key])],
      ]);
      return;
    }

    if (section.type === 'directorList') {
      y = drawDirectorTable(doc, y, section, formData?.[section.key] || []);
    }
  });

  return y;
};

/**
 * Generate a professionally designed Annual Report checklist PDF.
 * @returns {string} data URI string (base64)
 */
export const generateAnnualReportPDF = ({
  formData,
  checklist,
  service,
  userName = '',
}) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const state = service?.state || 'Florida';
  const entity = service?.entityType ? ` · ${service.entityType}` : '';
  const title = checklist?.title || `${state} Annual Report Checklist`;
  const subtitle = `${state}${entity}${service?.filingYear ? ` · Filing Year ${service.filingYear}` : ''}`;

  const startY = drawHeader(doc, {
    title,
    subtitle,
    preparedFor: userName,
  });

  if (checklist?.subtitle) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    const lines = doc.splitTextToSize(checklist.subtitle, 180);
    doc.text(lines, MARGIN, startY);
    drawSections(doc, startY + lines.length * 5 + 4, checklist, formData);
  } else {
    drawSections(doc, startY, checklist, formData);
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    addFooter(doc, i, pageCount);
  }

  return doc.output('datauristring', { filename: 'annual-report-checklist.pdf' });
};

export default generateAnnualReportPDF;
